import { supabase } from '../supabase';

/**
 * One weight record (migration 080, 17 October 2026): every weigh-in is one row in weight_logs.
 * The database keeps users.bodyweight_kg and Nutrition's profiles.current_weight_kg equal to the
 * latest weigh-in, so nothing here writes those two any more.
 */

const newRequestId = () =>
  (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-8xxx-xxxxxxxxxxxx'.replace(/x/g, () => Math.floor(Math.random() * 16).toString(16));

export { newRequestId };

/**
 * Records one weigh-in. Give the same `requestId` when retrying the same weigh-in: the database
 * refuses a second copy (unique), and this returns the one already stored if it is the same weight.
 * Throws on anything else, so a failed save is never reported as saved.
 *
 * @returns {Promise<{ id: string, weight_kg: number, logged_at: string }>}
 */
export async function recordWeighIn(userId, weightKg, {
  notes = null, checkInId = null, requestId = newRequestId(), loggedAt = null, source = 'fitness',
} = {}) {
  const row = {
    user_id: userId,
    weight_kg: weightKg,
    source,
    notes,
    request_id: requestId,
    ...(checkInId != null ? { check_in_id: checkInId } : {}),
    ...(loggedAt ? { logged_at: loggedAt } : {}),
  };
  const { data, error } = await supabase.from('weight_logs').insert(row).select('id, weight_kg, logged_at').single();
  if (!error) return data;

  // The same request again (a retry after a lost answer): the weigh-in is already stored
  if (error.code === '23505') {
    const { data: existing, error: readError } = await supabase
      .from('weight_logs')
      .select('id, weight_kg, logged_at')
      .eq('user_id', userId)
      .eq('request_id', requestId)
      .maybeSingle();
    if (!readError && existing && Number(existing.weight_kg) === Number(weightKg)) return existing;
  }
  const err = new Error(`Saving your weight: ${error.message || 'failed'}`);
  err.cause = error;
  throw err;
}

// The note sign-up writes on the starting weigh-in (OnboardingFlow)
export const SIGNUP_WEIGHT_NOTE = 'Starting weight from onboarding';

/**
 * The same starting weight from sign-up, stored more than once (a retried or repeated sign-up), is shown
 * once: the first. Only an exact repeat is hidden; nothing is deleted (owner, 20 October 2026; assistants
 * never change clients' data). Rows oldest first, as read.
 */
export const withoutRepeatedStartingWeight = (rows) => {
  const seen = new Set();
  return rows.filter((r) => {
    if (r.notes !== SIGNUP_WEIGHT_NOTE) return true;
    const key = Number(r.weight_kg).toFixed(2);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * Every weigh-in from both apps, oldest first, within the last `months` months.
 * Rows: { id, date, weight, source, checkInId }.
 */
export async function weighIns(userId, months = 6) {
  const since = new Date();
  since.setMonth(since.getMonth() - months);
  const { data, error } = await supabase
    .from('weight_logs')
    .select('id, logged_at, weight_kg, source, check_in_id, notes')
    .eq('user_id', userId)
    .gte('logged_at', since.toISOString())
    .order('logged_at', { ascending: true })
    .order('id', { ascending: true });
  if (error) throw error;
  return withoutRepeatedStartingWeight(data || []).map((r) => ({
    id: r.id, date: new Date(r.logged_at), weight: parseFloat(r.weight_kg), source: r.source, checkInId: r.check_in_id ?? null,
  }));
}

/** The person's first weigh-in ever ({ date, weight }), or null if there is none. */
export async function firstWeighIn(userId) {
  const { data, error } = await supabase
    .from('weight_logs')
    .select('logged_at, weight_kg')
    .eq('user_id', userId)
    .not('logged_at', 'is', null)
    .order('logged_at', { ascending: true })
    .order('id', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data ? { date: new Date(data.logged_at), weight: parseFloat(data.weight_kg) } : null;
}
